import { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';

interface RankSparklineProps {
  /** Array of rank positions over time (lower is better) */
  rankHistory: number[];
  /** Optional max rank to use as scale ceiling */
  maxRank?: number;
  /** Width in pixels */
  width?: number;
  /** Height in pixels */
  height?: number;
  /** CSS class name */
  className?: string;
  /** Show tooltip on hover */
  showTooltip?: boolean;
}

/**
 * RankSparkline - Mini line chart showing user's rank history.
 *
 * Displays rank progression over a competition period.
 * Lower ranks are better, so the chart is inverted (lower = higher on chart).
 * Uses colorblind-safe green for improvement, amber for decline.
 */
export default function RankSparkline({
  rankHistory,
  maxRank = 100,
  width = 80,
  height = 24,
  className = '',
  showTooltip = true,
}: RankSparklineProps) {
  // Convert rank array to chart data with inverted Y axis
  const data = useMemo(() => {
    if (!rankHistory || rankHistory.length === 0) return [];
    return rankHistory.map((rank, i) => ({
      index: i + 1,
      rank,
      // Invert for display (rank 1 at top)
      displayRank: maxRank - rank + 1,
    }));
  }, [rankHistory, maxRank]);

  // Determine color based on rank trend
  const trendColor = useMemo(() => {
    if (data.length < 2) return '#6b7280'; // gray-500
    const first = data[0].rank;
    const last = data[data.length - 1].rank;
    // Lower rank is better
    if (last < first) return '#22c55e'; // green-500 (improved)
    if (last > first) return '#f59e0b'; // amber-500 (declined)
    return '#6b7280'; // gray-500 (no change)
  }, [data]);

  if (data.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-gray-600 text-xs ${className}`}
        style={{ width, height }}
      >
        --
      </div>
    );
  }

  return (
    <div className={className} style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line
            type="monotone"
            dataKey="displayRank"
            stroke={trendColor}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
          {showTooltip && (
            <Tooltip
              contentStyle={{
                background: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '4px',
                padding: '4px 8px',
              }}
              labelStyle={{ display: 'none' }}
              formatter={(value: number, name: string, props) => {
                const rank = props?.payload?.rank ?? value;
                return [`#${rank}`, 'Rank'];
              }}
              cursor={{ stroke: '#4b5563', strokeWidth: 1 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * RankTrendIndicator - Simple up/down arrow with color for rank change.
 */
export function RankTrendIndicator({
  current,
  previous,
  className = '',
}: {
  current: number;
  previous: number;
  className?: string;
}) {
  const diff = previous - current; // Positive = improved (moved up)

  if (diff === 0) {
    return <span className={`text-gray-500 ${className}`}>--</span>;
  }

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        diff > 0 ? 'text-green-400' : 'text-amber-400'
      } ${className}`}
    >
      {diff > 0 ? (
        <>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
          {diff}
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          {Math.abs(diff)}
        </>
      )}
    </span>
  );
}
